import type { CompareCell } from "../api/forecast_api"
import type { SalesDataPoint, SalesHierarchyNode, SalesMetrics } from "../types"

function num(x: string | number | undefined | null): number {
  if (x === undefined || x === null || x === "") return 0
  const n = typeof x === "number" ? x : Number(x)
  return Number.isFinite(n) ? n : 0
}

/** True when the API sent a concrete value (omit `null` — serde may serialize `None` as null). */
function cell_volume_defined(x: string | number | undefined | null): boolean {
  return x !== undefined && x !== null && x !== ""
}

function periodFromKey(period_key: string): { year: number; month: number; period_key: string } {
  const year = Number(period_key.slice(0, 4))
  const month = Number(period_key.slice(5, 7))
  return { period_key, year: Number.isFinite(year) ? year : 0, month: Number.isFinite(month) ? month : 0 }
}

function applyCompareCellToDataPoint(dp: SalesDataPoint, cell: CompareCell): SalesDataPoint {
  // Dollar compare only when QuickBooks sent an actual $ amount — avoids showing forecast vs forecast.
  if (!cell_volume_defined(cell.actual_volume_dollars)) {
    return dp
  }

  const actual_vol = num(cell.actual_volume_dollars)
  // Forecast line must match Forecast tab: use the hierarchy point's displayed `metrics` (scenario /
  // opportunity-adjusted). Server compare `forecast_volume_dollars` is raw DB and can diverge.
  const baseline_forecast_vol = dp.metrics.volume_dollars
  const has_meaningful_baseline_forecast = Math.abs(baseline_forecast_vol) > 1e-6
  const forecast_vol = has_meaningful_baseline_forecast
    ? baseline_forecast_vol
    : cell_volume_defined(cell.forecast_volume_dollars)
      ? num(cell.forecast_volume_dollars)
      : baseline_forecast_vol

  const forecast_metrics: SalesMetrics = {
    ...dp.metrics,
    volume_dollars: forecast_vol,
  }
  const actual_metrics: SalesMetrics = {
    ...dp.metrics,
    volume_dollars: actual_vol,
    cases_sold: cell.actual_cases != null ? cell.actual_cases : dp.metrics.cases_sold,
    units_sold: cell.actual_units != null ? cell.actual_units : null,
  }

  const volume_dollars_variance = actual_vol - forecast_vol
  const volume_dollars_variance_pct =
    forecast_vol !== 0 ? (volume_dollars_variance / forecast_vol) * 100 : 0

  const has_case_pair = cell.forecast_cases != null && cell.actual_cases != null
  const cases_sold_variance = has_case_pair ? cell.actual_cases! - cell.forecast_cases! : 0
  const cases_sold_variance_pct =
    has_case_pair && cell.forecast_cases! !== 0
      ? ((cell.actual_cases! - cell.forecast_cases!) / cell.forecast_cases!) * 100
      : 0

  const variance = {
    store_count_variance: 0,
    volume_dollars_variance,
    cases_sold_variance,
    store_count_variance_pct: 0,
    volume_dollars_variance_pct,
    cases_sold_variance_pct,
  }

  return { ...dp, forecast_metrics, actual_metrics, variance, compare_axis: "dollars" as const }
}

/**
 * Merge server-computed compare cells into the hierarchy's leaf sales_data so
 * the existing ComparisonMetrics UI lights up. Cells are keyed by
 * (node_id, period). Non-leaf rows are unaffected — the table aggregates them
 * at render time.
 */
export function mergeCompareCellsIntoHierarchy(
  roots: SalesHierarchyNode[],
  cells: CompareCell[]
): SalesHierarchyNode[] {
  const byNode = new Map<string, Map<string, CompareCell>>()
  for (const cell of cells) {
    let map = byNode.get(cell.node_id)
    if (!map) {
      map = new Map()
      byNode.set(cell.node_id, map)
    }
    map.set(cell.period, cell)
  }

  function walk(node: any): any {
    const cellMap = byNode.get(node.id)
    const next: any = { ...node }
    // Compare cells are keyed by leaf node_id (e.g. SKU). Many leaves have no `sales_data` until
    // actuals are persisted — forecast rows may live only on parents. Union cell periods with
    // existing points so preview compare data is not dropped.
    if (cellMap && cellMap.size > 0) {
      const byPeriod = new Map<string, SalesDataPoint>()
      for (const dp of node.sales_data ?? []) {
        byPeriod.set(dp.period.period_key, dp)
      }
      for (const periodKey of cellMap.keys()) {
        if (!byPeriod.has(periodKey)) {
          byPeriod.set(periodKey, {
            period: periodFromKey(periodKey),
            metrics: {
              store_count: 0,
              volume_dollars: 0,
              cases_sold: 0,
              case_cost: 0,
            },
          } as SalesDataPoint)
        }
      }
      next.sales_data = [...byPeriod.values()]
        .sort((a, b) => a.period.period_key.localeCompare(b.period.period_key))
        .map((dp: SalesDataPoint): SalesDataPoint => {
          const cell = cellMap.get(dp.period.period_key)
          if (!cell) return dp
          return applyCompareCellToDataPoint(dp, cell)
        })
    }
    if (Array.isArray(node.children) && node.children.length > 0) {
      next.children = node.children.map(walk)
    }
    return next
  }

  return roots.map(walk) as SalesHierarchyNode[]
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("mergeCompareCellsIntoHierarchy", () => {
    function point(period_key: string, vol: number): SalesDataPoint {
      return {
        period: { year: 2026, month: 1, period_key },
        metrics: { store_count: 0, volume_dollars: vol, cases_sold: 0, case_cost: 0 },
      }
    }

    it("populates forecast/actual/variance on matching leaves", () => {
      const tree: SalesHierarchyNode[] = [{
        id: "n1",
        name: "Sku A",
        level: "sku" as any,
        sales_data: [point("2026-01", 100)],
      } as any]
      const cells: CompareCell[] = [{
        node_id: "n1",
        period: "2026-01",
        forecast_volume_dollars: "120",
        actual_volume_dollars: "130",
        variance_volume_dollars: "10",
        variance_volume_pct: 0.1,
      }]
      const merged = mergeCompareCellsIntoHierarchy(tree, cells)
      const dp = merged[0].sales_data[0]
      expect(dp.forecast_metrics?.volume_dollars).toBe(100)
      expect(dp.actual_metrics?.volume_dollars).toBe(130)
      expect(dp.variance?.volume_dollars_variance).toBe(30)
      expect(dp.variance?.volume_dollars_variance_pct).toBe(30)
      expect(dp.compare_axis).toBe("dollars")
    })

    it("skips compare when the cell has no actual dollars", () => {
      const tree: SalesHierarchyNode[] = [{
        id: "n1",
        name: "Sku A",
        level: "sku" as any,
        sales_data: [point("2026-01", 100)],
      } as any]
      const cells: CompareCell[] = [{
        node_id: "n1",
        period: "2026-01",
        forecast_volume_dollars: "120",
      }]
      const merged = mergeCompareCellsIntoHierarchy(tree, cells)
      const dp = merged[0].sales_data[0]
      expect(dp.variance).toBeUndefined()
      expect(dp.compare_axis).toBeUndefined()
    })

    it("leaves cells without a match untouched", () => {
      const tree: SalesHierarchyNode[] = [{
        id: "n1",
        name: "Sku A",
        level: "sku" as any,
        sales_data: [point("2026-01", 100)],
      } as any]
      const merged = mergeCompareCellsIntoHierarchy(tree, [])
      expect(merged[0].sales_data[0].variance).toBeUndefined()
    })

    it("injects sales_data from compare cells when the leaf had no points (preview before persist)", () => {
      const tree: SalesHierarchyNode[] = [{
        id: "leaf-sku",
        name: "Yuzu 6pk",
        level: "sku" as any,
        sales_data: [],
      } as any]
      const cells: CompareCell[] = [{
        node_id: "leaf-sku",
        period: "2026-01",
        forecast_volume_dollars: "200",
        actual_volume_dollars: "150",
        forecast_cases: 10,
        actual_cases: 8,
        variance_volume_dollars: "-50",
        variance_volume_pct: -0.25,
      }]
      const merged = mergeCompareCellsIntoHierarchy(tree, cells)
      expect(merged[0].sales_data).toHaveLength(1)
      const dp = merged[0].sales_data[0]
      expect(dp.forecast_metrics?.volume_dollars).toBe(200)
      expect(dp.actual_metrics?.volume_dollars).toBe(150)
      expect(dp.variance?.volume_dollars_variance).toBe(-50)
      expect(dp.compare_axis).toBe("dollars")
    })
  })
}
